import { TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { StateView } from '@/components/ui/Feedback';
import type { AiChatFault } from '@/store/aiChatStore';

/** Восстановление под каждый вид сбоя: из ошибки всегда есть выход. */
export const faults: Record<AiChatFault, { title: string; description: string; action: string }> = {
  not_found: {
    title: 'Диалог не найден',
    description: 'Чат по этому заданию не открыт. Начните его заново — ответы не потеряются.',
    action: 'Открыть диалог',
  },
  completed: {
    title: 'Задание уже сдано',
    description: 'Диалог по этому заданию завершён. Можно начать новый разбор.',
    action: 'Начать новый диалог',
  },
  busy: {
    title: 'Ассистент занят прошлой задачей',
    description:
      'Предыдущий запрос ещё выполняется, поэтому новый не принимается. Начните диалог заново — ответы из переписки останутся у ассистента.',
    action: 'Начать диалог заново',
  },
  needs_preview: {
    title: 'Сначала соберите итог',
    description:
      'Ассистент ещё не собрал итоговый материал. Нажмите «Сформировать итог» и дождитесь файла, затем отправляйте.',
    action: 'Понятно',
  },
  file_missing: {
    title: 'Вложение недоступно',
    description: 'Файл больше не хранится на сервере. Обновите переписку и попробуйте ещё раз.',
    action: 'Обновить переписку',
  },
  assignment_missing: {
    title: 'Диалог по этому заданию недоступен',
    description:
      'Сервер не открывает диалог: задание не привязано к вашему курсу. Это на стороне сервиса — попробуйте позже или напишите в поддержку.',
    action: 'Попробовать снова',
  },
  unknown: {
    title: 'Диалог не открылся',
    description: 'Сервер не ответил. Проверьте подключение и попробуйте ещё раз.',
    action: 'Попробовать снова',
  },
};

/** Экран вместо диалога, когда открыть его не удалось. */
export function ChatFaultView({ fault, onRetry }: { fault: AiChatFault; onRetry: () => void }) {
  const navigate = useNavigate();
  const state = faults[fault];

  return (
    <StateView
      icon={<TriangleAlert className="h-6 w-6" aria-hidden />}
      title={state.title}
      description={state.description}
      primary={
        <Button variant="primary" block onClick={onRetry}>
          {state.action}
        </Button>
      }
      secondary={
        <Button variant="ghost" block onClick={() => navigate('/help')}>
          Написать в поддержку
        </Button>
      }
    />
  );
}
